/* eslint-disable no-nested-ternary */
import { Flex, VStack } from '@chakra-ui/react'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useModalState } from '~/atoms/ModalState'
import EmptySearch from '~/components/EmptySearch'
import { EmptyState } from '~/components/EmptyState'
import { useTogglePanel } from '~/components/layout/ResponsivePage'
import { ScrollingList } from '~/components/shared/ScrollingList'
import { SearchInput } from '~/components/shared/SearchInput'
import { FaxListItem } from '~/pages/Fax/FaxListItem'
import { FaxDirectionFilter } from '~/types/ApiRequestTypes'
import { SearchValue } from '~/types/Shared'
import { hasNoValueOrIsWhitespace } from '~/utils/common'

import type { FaxInstance } from '@mangovoice/types'

import { useFaxViewModel } from './Fax.viewmodel'
import FaxListFilters from './FaxListFilters'

export const FaxListView = ({
  state,
  actions,
  utils,
}: ReturnType<typeof useFaxViewModel>) => {
  const {
    faxModal: [faxModalState, setFaxModalState],
  } = useModalState()
  const {
    faxes,
    faxesLoading: faxesLoadingState,
    selectedFaxId,
    directionFilter,
    searchValue,
    extensionFilter,
    statusFilter,
  } = state
  const { t } = useTranslation()

  const {
    state: { isOpen: isTogglePanelOpen },
    togglePanelVisibility,
  } = useTogglePanel()

  useEffect(() => {
    if (!state.selectedFaxId && !isTogglePanelOpen) {
      togglePanelVisibility(true)
    }
  }, [])

  const handleNewFaxClicked = () => {
    setFaxModalState({
      ...faxModalState,
      open: true,
      number: utils.getNumber(state.selectedFaxInstance),
    })
    window.gtag('event', 'quick_action_fax_reply', {})
  }
  const handleScrollEndReached = () => {
    actions.tryGetNextFaxPage({ cancelRefetch: false })
  }

  const handleSearchValueChanged = (searchValue: SearchValue) => {
    actions.setSearchValue(searchValue)
  }

  const listContent = useMemo(() => {
    const faxesEmpty = faxes.length === 0
    const searchEmpty = hasNoValueOrIsWhitespace(searchValue?.value)
    const shouldShowEmptySearchResults =
      !searchEmpty && !faxesLoadingState && faxesEmpty
    const shouldShowNoFaxes = searchEmpty && !faxesLoadingState && faxesEmpty

    if (shouldShowNoFaxes) {
      const areFiltersApplied =
        directionFilter !== FaxDirectionFilter.ALL ||
        extensionFilter !== 'all' ||
        statusFilter !== 'all'
      const title = areFiltersApplied
        ? t('faxViewList.titleNoMatchingFaxes')
        : t('faxViewList.titleNoFaxes')
      const subtitle = areFiltersApplied
        ? t('faxViewList.subtitleTryRemovingFilters')
        : t('faxViewList.subtitleNoFaxes')
      const buttonText = areFiltersApplied
        ? t('faxViewList.buttonTextStartFax')
        : t('faxViewList.buttonTextStartFirstFax')

      return (
        <Flex
          direction="column"
          alignContent="flex-start"
          height="100%"
          bg="gray.200"
          width="100%"
        >
          <EmptyState
            title={title}
            subtitle={subtitle}
            buttonText={buttonText}
            onClick={handleNewFaxClicked}
          />
        </Flex>
      )
    }

    if (shouldShowEmptySearchResults) {
      return <EmptySearch search={searchValue?.value} />
    }

    return (
      <ScrollingList
        display="flex"
        width="full"
        height="full"
        backgroundColor="gray.200"
        py="0.75rem"
        pl="0.5rem"
        loadThresholdPercentage={0.25}
        showLoaderFooter={faxesLoadingState}
        scrollbarOptions={{ showOnHoverOnly: true }}
        onEndReached={handleScrollEndReached}
        itemData={faxes}
        onBuildItemContent={(_, fax) => {
          return (
            <FaxListItem
              fax={fax as FaxInstance}
              isSelected={selectedFaxId === fax.id}
              searchTerm={searchValue?.value}
            />
          )
        }}
      />
    )
  }, [
    faxes,
    directionFilter,
    faxesLoadingState,
    selectedFaxId,
    searchValue,
    statusFilter,
  ])

  return (
    <VStack display="flex" height="100%" alignSelf="stretch" spacing="2px">
      <Flex
        width="100%"
        padding="1rem"
        justifyContent="center"
        alignItems="center"
        gap="0.75rem"
        alignSelf="stretch"
        backgroundColor="gray.200"
      >
        <SearchInput
          placeholder={t('faxViewList.searchPlaceholder')}
          onChange={handleSearchValueChanged}
          value={searchValue?.label ?? ''}
        />
      </Flex>
      <FaxListFilters />
      {listContent}
    </VStack>
  )
}
